import { Avatar, Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { VariableKeyBox } from "./key-box";
import { useState } from "react";
import { cn } from "@/utils/theme";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { FormVariables } from "./form";
import { DataCenterVariables } from "./data-center";
import { CalculatedFieldVariables } from "./calculated-field";
import { DocGeneratorVariables } from "./doc-generator";
import { InteroperabilityVariables } from "./interoperability";
import { PaymentVariables } from "./payment";
import { N8NVariables } from "./n8n";
import {
  ExecutionStartTimestamp,
  ExecutionEndTimestamp,
} from "./execution-timestamps";
import { SingDocumentVariables } from "./sing-document";
import { ExpedienteVariables } from "./expediente";
import { MergeVariables } from "./merge";

const nodeTypes = (node: string) => {
  switch (node) {
    case NodeTypes.INITIALIZER:
      return [
        "neutral",
        "play",
        "bg-neutral-object-accent-silent border-neutral text-body-quiet",
        "Inicio del Flujo",
      ] as const;
    case NodeTypes.FORM:
      return [
        "action",
        "bars",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Formulario",
      ] as const;
    case NodeTypes.IF:
      return [
        "neutral",
        "split",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador IF",
      ] as const;
    case NodeTypes.SWITCH:
      return [
        "neutral",
        "arrows-cross",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador Switch",
      ] as const;
    case NodeTypes.CALCULATED:
      return [
        "success",
        "function",
        "bg-surface-sucess-accent-quiet border-contextual-success text-success",
        "Campo Calculado",
      ] as const;
    case NodeTypes.ASSIGN_GROUP:
      return [
        "info",
        "users",
        "bg-surface-info-accent-quiet border-contextual-info text-info",
        "Asignar Grupo",
      ] as const;
    case NodeTypes.DATA_CENTER:
      return [
        "info",
        "table",
        "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
        "Centro de Datos",
      ] as const;
    case NodeTypes.DOC_GENERATOR:
      return [
        "action",
        "file-alt",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Generador de Documentos",
      ] as const;
    case NodeTypes.PAYMENT:
      return [
        "warning",
        "dollar-sign",
        "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
        "Pasarela de Pago",
      ] as const;
    case NodeTypes.INTEROPERABILITY:
      return [
        "warning",
        "diagram-nested",
        "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
        "Interoperabilidad",
      ] as const;
    case NodeTypes.N8N:
      return [
        "warning",
        "hexagon-nodes",
        "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
        "Nodo n8n",
      ] as const;
    case NodeTypes.SIGN_DOCUMENT:
      return [
        "action",
        "signature",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Firma digital",
      ] as const;

    case NodeTypes.EXPEDIENTE:
      return [
        "info",
        "folder-open",
        "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
        "Expediente Digital",
      ] as const;
    case NodeTypes.RESOLUTION:
      return [
        "action",
        "file-circle-plus",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Resolución",
      ] as const;
    case NodeTypes.MERGE:
      return [
        "neutral",
        "code-merge",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador Merge",
      ] as const;
    default:
      return [
        "neutral",
        "play",
        "bg-neutral-object-accent-silent border-neutral text-body-quiet",
        "Desconocido",
      ] as const;
  }
};

interface Props {
  id: string;
  display_name?: string;
  nodeType: (typeof NodeTypes)[keyof typeof NodeTypes];
}

export const InputOutputBlock = () => {
  return (
    <div className="flex">
      <div className="w-0.5 relative last:bg-transparent bg-neutral-object-accent-quiet translate-x-[25px]">
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent-quiet rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-surface-info-accent-quiet rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <h3 className="mb-4 space-x-2">
            <span className="text-body-quiet">Paso 1</span>
            <span className="text-body-loud">nombre-del-paso</span>
          </h3>
          <div className="space-y-3">
            <VariableKeyBox type="array" />
            <VariableKeyBox type="string" />
            <VariableKeyBox type="number" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const InputOutput = ({ nodeType, display_name, id }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [type, icon, className, label] = nodeTypes(nodeType);

  return (
    <div className="p-4 pt-8">
      <div
        className={cn(
          "pl-2.5 space-x-2 flex items-center justify-start",
          isOpen ? "pb-0" : "pb-8",
        )}
      >
        <div className="bg-transparent p-1 border border-neutral-quiet rounded-[12px]">
          <Avatar
            size="2xs"
            shape={null}
            className={`items-center justify-center border rounded-lg ${className}`}
          >
            <div className="">
              <Icon type="fas" name={icon} size="sm" className="" />
            </div>
          </Avatar>
        </div>
        <div className="flex w-full items-center space-x-2 min-w-0">
          <Badge size="sm" variant={type}>
            {label}
          </Badge>

          <div className="flex-1 font-semibold text-body-loud truncate">
            {display_name}
          </div>

          <Button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle"
            size="icon"
            variant="ghost"
            className={`ml-auto mr-3 ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <Icon name="angle-up" type="fas" />
          </Button>
        </div>
      </div>

      <div
        id="content"
        className={`
        relative overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "min-h-full mb-4" : "max-h-0"}
        `}
      >
        <ExecutionStartTimestamp
          nodeId={id}
          nodeName={display_name || "Nodo"}
        />

        {nodeType === NodeTypes.FORM && <FormVariables id={id} />}
        {nodeType === NodeTypes.DATA_CENTER && <DataCenterVariables id={id} />}
        {nodeType === NodeTypes.CALCULATED && (
          <CalculatedFieldVariables id={id} />
        )}
        {nodeType === NodeTypes.DOC_GENERATOR && (
          <DocGeneratorVariables id={id} />
        )}
        {nodeType === NodeTypes.INTEROPERABILITY && (
          <InteroperabilityVariables id={id} />
        )}
        {nodeType === NodeTypes.PAYMENT && <PaymentVariables id={id} />}
        {nodeType === NodeTypes.N8N && <N8NVariables id={id} />}
        {nodeType === NodeTypes.SIGN_DOCUMENT && (
          <SingDocumentVariables id={id} />
        )}
        {nodeType === NodeTypes.EXPEDIENTE && <ExpedienteVariables id={id} />}
        {nodeType === NodeTypes.MERGE && <MergeVariables id={id} />}

        <ExecutionEndTimestamp nodeId={id} nodeName={display_name || "Nodo"} />
      </div>
    </div>
  );
};
